import { csrf_headers, get_token, get_user_id, getCookie } from '@/router/index.js';
import { SYSTEM_MANAGEMENT_ENDPOINTS } from '@/constants/system_management_endpoints.js';

const SERVICE_WORKER_PATH = '/aic-one-push-sw.js';
const HEARTBEAT_INTERVAL_MS = 45000;
const PERMISSION_PROMPTED_KEY = 'aic_one_push_permission_prompted';

let service_worker_registration = null;
let heartbeat_interval = null;
let heartbeat_started_for_token = null;
let unload_listener_registered = false;
let initialization_in_flight = null;

function endpoint_url(path) {
  return `${process.env.VUE_APP_API_BASE_URL}${path}`;
}

function save_subscription_url() {
  return endpoint_url(
    SYSTEM_MANAGEMENT_ENDPOINTS.save_push_subscription
  );
}

function heartbeat_url() {
  return endpoint_url(
    SYSTEM_MANAGEMENT_ENDPOINTS.push_heartbeat
  );
}

function mark_offline_url() {
  return endpoint_url(
    SYSTEM_MANAGEMENT_ENDPOINTS.mark_push_offline
  );
}

function is_authenticated_for_push() {
  const token = get_token();
  if (!token) return false;

  const otp_required = localStorage.getItem('otp_required') === '1';
  const otp_verified = localStorage.getItem('otp_verified') === '1';
  return !otp_required || otp_verified;
}

function push_is_supported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function url_base64_to_uint8_array(base64_string) {
  const padding = '='.repeat((4 - (base64_string.length % 4)) % 4);
  const base64 = (base64_string + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw_data = window.atob(base64);
  return Uint8Array.from([...raw_data].map(char => char.charCodeAt(0)));
}

function build_auth_headers(token) {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Token ${token}`,
    'X-Page-URL': window.location.href,
    ...csrf_headers(getCookie('csrftoken')),
  };
}

function build_presence_payload(extra = {}) {
  return {
    user_id: get_user_id(),
    page_url: window.location.href,
    user_agent: navigator.userAgent,
    occurred_at: new Date().toISOString(),
    ...extra,
  };
}

async function register_service_worker() {
  if (service_worker_registration) return service_worker_registration;

  service_worker_registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH);
  if (navigator.serviceWorker.ready) {
    service_worker_registration = await navigator.serviceWorker.ready;
  }

  return service_worker_registration;
}

async function ensure_notification_permission() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  if (localStorage.getItem(PERMISSION_PROMPTED_KEY) === '1') return false;

  localStorage.setItem(PERMISSION_PROMPTED_KEY, '1');
  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

async function create_push_subscription(registration) {
  const vapid_public_key = process.env.VUE_APP_VAPID_PUBLIC_KEY;
  if (!vapid_public_key) return null;

  const existing_subscription = await registration.pushManager.getSubscription();
  if (existing_subscription) return existing_subscription;

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: url_base64_to_uint8_array(vapid_public_key),
  });
}

async function save_subscription(subscription) {
  const token = get_token();
  if (!token || !subscription) return false;
  const subscription_data = subscription.toJSON ? subscription.toJSON() : subscription;

  const response = await fetch(save_subscription_url(), {
    method: 'POST',
    credentials: 'include',
    headers: build_auth_headers(token),
    body: JSON.stringify({
      endpoint: subscription_data.endpoint,
      p256dh: subscription_data.keys.p256dh,
      auth: subscription_data.keys.auth,
    }),
  });

  return response.ok;
}

async function send_heartbeat() {
  const token = get_token();
  if (!token) return false;

  const response = await fetch(heartbeat_url(), {
    method: 'POST',
    credentials: 'include',
    headers: build_auth_headers(token),
    body: JSON.stringify(build_presence_payload({ status: 'online' })),
  });

  return response.ok;
}

export function start_push_heartbeat() {
  const token = get_token();
  if (!token || heartbeat_started_for_token === token) return;

  stop_push_heartbeat();
  heartbeat_started_for_token = token;
  send_heartbeat().catch(() => undefined);
  heartbeat_interval = window.setInterval(() => {
    send_heartbeat().catch(() => undefined);
  }, HEARTBEAT_INTERVAL_MS);
}

export function stop_push_heartbeat() {
  if (heartbeat_interval) {
    window.clearInterval(heartbeat_interval);
    heartbeat_interval = null;
  }
  heartbeat_started_for_token = null;
}

export function send_push_offline_beacon(reason = 'page_unload') {
  const token = localStorage.getItem('token');
  if (!token) return;

  const payload = build_presence_payload({
    status: 'offline',
    reason,
    token,
  });
  const body = JSON.stringify(payload);
  const url = mark_offline_url();

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    if (navigator.sendBeacon(url, blob)) return;
  }

  fetch(url, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: build_auth_headers(token),
    body,
  }).catch(() => undefined);
}

function register_unload_listener() {
  if (unload_listener_registered) return;
  unload_listener_registered = true;
  window.addEventListener('pagehide', () => send_push_offline_beacon('pagehide'));
}

export function shutdown_push_notifications(reason = 'logout') {
  send_push_offline_beacon(reason);
  stop_push_heartbeat();
  initialization_in_flight = null;
}

export async function initialize_push_notifications() {
  if (!is_authenticated_for_push()) return false;
  if (!push_is_supported()) {
    start_push_heartbeat();
    register_unload_listener();
    return false;
  }

  if (initialization_in_flight) return initialization_in_flight;

  initialization_in_flight = (async () => {
    const registration = await register_service_worker();
    const permission_granted = await ensure_notification_permission();
    if (!permission_granted) {
      start_push_heartbeat();
      register_unload_listener();
      return false;
    }

    const subscription = await create_push_subscription(registration);
    if (subscription) {
      await save_subscription(subscription);
    }

    start_push_heartbeat();
    register_unload_listener();
    return true;
  })().catch(error => {
    console.warn('Push notifications could not be initialized:', error);
    start_push_heartbeat();
    register_unload_listener();
    return false;
  }).finally(() => {
    initialization_in_flight = null;
  });

  return initialization_in_flight;
}

export function sync_push_notifications_for_auth_state() {
  if (!is_authenticated_for_push()) {
    stop_push_heartbeat();
    return;
  }

  initialize_push_notifications();
}
